import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listAllDocuments, getDocumentDetails } from '../api/client';
import { getRememberedRivhitDocumentLink } from '../lib/rivhitDocumentsSync';
import { useRivhitData, type RivhitCustomer } from '../context/RivhitDataContext';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import {
  clearRivhitDocumentsDirty,
  getRivhitDocumentsDirtyAt,
  subscribeToRivhitDocumentsDirty,
} from '../lib/rivhitDocumentsSync';

interface UnifiedDocumentItem {
  view_type?: 'document' | 'customer';
  document_category: string;
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  document_time?: string;
  amount: number;
  paid_amount?: number;
  open_amount?: number;
  invoices_count?: number;
  customer_id?: number;
  customer_name: string;
  document_link?: string;
  documents?: UnifiedDocumentItem[];
}

interface DocumentLineItem {
  item_id?: number;
  description?: string;
  catalog_number?: string;
  quantity: number;
  price_nis?: number;
  total_nis?: number;
}

type CategoryFilter = 'all' | 'invoice' | 'receipt' | 'delivery_note';
type ViewMode = 'documents' | 'customer';
type DocumentSortField = 'document_number' | 'document_date' | 'customer_name' | 'amount' | 'open_amount' | 'invoices_count';
type SortDirection = 'asc' | 'desc';
const DOCUMENTS_PAGE_SIZE = 100;

const CATEGORY_BADGES: Record<string, { bg: string; text: string; labelKey: string }> = {
  invoice: { bg: 'bg-blue-100', text: 'text-blue-700', labelKey: 'documents.categoryInvoice' },
  receipt: { bg: 'bg-green-100', text: 'text-green-700', labelKey: 'documents.categoryReceipt' },
  delivery_note: { bg: 'bg-orange-100', text: 'text-orange-700', labelKey: 'documents.categoryDeliveryNote' },
};

function toIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayIsoDate(): string {
  return toIsoDate(new Date());
}

function getDaysAgoIsoDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return toIsoDate(date);
}

function getStartOfWeek(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday as start of week
  const start = new Date(now);
  start.setDate(now.getDate() - diff);
  return toIsoDate(start);
}

function getStartOfMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

function getStartOfPreviousMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}

function getEndOfPreviousMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 0));
}

function parseDocumentDate(value: string): number {
  const normalized = value.replace(/\//g, '');
  if (normalized.length !== 8) return 0;
  const day = normalized.slice(0, 2);
  const month = normalized.slice(2, 4);
  const year = normalized.slice(4, 8);
  return Number(`${year}${month}${day}`);
}

function parseDocumentTime(value?: string): number {
  if (!value) return 0;
  const normalized = value.replace(/:/g, '').trim();
  const digits = normalized.replace(/\D/g, '');
  if (!digits) return 0;
  return Number(digits.padEnd(6, '0').slice(0, 6));
}

export default function Documents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    configured,
    customers,
    ensureCustomersLoaded,
  } = useRivhitData();

  // Data
  const [documents, setDocuments] = useState<UnifiedDocumentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('documents');
  const [openOnly, setOpenOnly] = useState(false);
  const [sortField, setSortField] = useState<DocumentSortField>('document_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [customerSearch, setCustomerSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(getTodayIsoDate);
  const [dateTo, setDateTo] = useState(getTodayIsoDate);
  const [selectedCustomer, setSelectedCustomer] = useState<RivhitCustomer | null>(null);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<number>>(new Set());
  const [customerDocuments, setCustomerDocuments] = useState<Record<number, UnifiedDocumentItem[]>>({});
  const [customerDocumentsLoading, setCustomerDocumentsLoading] = useState<Set<number>>(new Set());

  // Document row expand state (for document view mode)
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [docLineItems, setDocLineItems] = useState<Record<string, DocumentLineItem[]>>({});
  const [docLineItemsLoading, setDocLineItemsLoading] = useState<Set<string>>(new Set());

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const handledDirtyAtRef = useRef<number | null>(null);

  const formatDateForApi = (isoDate: string): string | undefined => {
    if (!isoDate) return undefined;
    const [y, m, d] = isoDate.split('-');
    return `${d}${m}${y}`;
  };

  const handleDateFromChange = (nextFrom: string) => {
    setDateFrom(nextFrom);
    if (dateTo && nextFrom > dateTo) {
      setDateTo(nextFrom);
    }
  };

  const handleDateToChange = (nextTo: string) => {
    setDateTo(nextTo);
    if (dateFrom && nextTo < dateFrom) {
      setDateFrom(nextTo);
    }
  };

  const buildParams = useCallback(() => {
    const params: Record<string, unknown> = {};
    if (categoryFilter !== 'all') params.category_filter = categoryFilter;
    if (customerSearch.trim()) params.customer_name = customerSearch.trim();
    if (openOnly) params.open_only = true;
    if (viewMode === 'customer') params.group_by = 'customer';
    const fd = formatDateForApi(dateFrom);
    const td = formatDateForApi(dateTo);
    if (fd) params.from_date = fd;
    if (td) params.to_date = td;
    params.limit = DOCUMENTS_PAGE_SIZE;
    params.offset = pageOffset;
    return params;
  }, [categoryFilter, customerSearch, dateFrom, dateTo, openOnly, pageOffset, viewMode]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);

    const t0 = performance.now();
    console.log(`[Documents] 📄 loadDocuments start`, { timestamp: new Date().toISOString() });

    try {
      const res = await listAllDocuments(buildParams() as Record<string, string | number | boolean>);
      const t1 = performance.now();
      const data = res.data;
      const mergeRememberedLinks = (item: UnifiedDocumentItem): UnifiedDocumentItem => {
        const rememberedLink = getRememberedRivhitDocumentLink(item.document_type, item.document_number);
        const mergedItem = {
          ...item,
          document_link: item.document_link || rememberedLink,
          documents: item.documents?.map(mergeRememberedLinks),
        };
        if (!mergedItem.document_link) {
          console.log(
            `[RivhitDocs] Documents row has no link after merge category=${item.document_category} ` +
            `type=${item.document_type} number=${item.document_number} date=${item.document_date} ` +
            `time=${item.document_time || 'NONE'} sourceHasLink=${Boolean(item.document_link)} ` +
            `rememberedHasLink=${Boolean(rememberedLink)} ts=${new Date().toISOString()}`
          );
        } else {
          console.log(
            `[RivhitDocs] Documents row link resolved category=${item.document_category} ` +
            `type=${item.document_type} number=${item.document_number} sourceHasLink=${Boolean(item.document_link)} ` +
            `rememberedHasLink=${Boolean(rememberedLink)} finalLink=${mergedItem.document_link} ` +
            `ts=${new Date().toISOString()}`
          );
        }
        return mergedItem;
      };
      const mergedItems = (data.items ?? []).map(mergeRememberedLinks);
      console.log(`[Documents] ✅ loadDocuments API returned in ${(t1 - t0).toFixed(1)}ms — ${mergedItems.length} items, total=${data.total_count ?? 0}, offset=${data.offset ?? 0}, hasMore=${Boolean(data.has_more)}`, { timestamp: new Date().toISOString() });
      setDocuments(mergedItems);
      setTotalCount(data.total_count ?? 0);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error(`[Documents] ❌ loadDocuments failed after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
    } finally {
      setLoading(false);
      console.log(`[Documents] 🏁 loadDocuments complete — total elapsed ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
    }
  }, [buildParams]);

  const refreshDocuments = useCallback(async () => {
    await loadDocuments();
  }, [loadDocuments]);

  const refreshPageData = useCallback(async () => {
    await refreshDocuments();
  }, [refreshDocuments]);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  useEffect(() => {
    setPageOffset(0);
    setExpandedDocs(new Set());
    setExpandedCustomers(new Set());
    setCustomerDocuments({});
    setCustomerDocumentsLoading(new Set());
    setDocLineItems({});
    setDocLineItemsLoading(new Set());
  }, [categoryFilter, customerSearch, dateFrom, dateTo, openOnly, viewMode]);

  // Reload documents when server-side filters or pagination change (debounced for safety)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadDocuments();
    }, 150);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [loadDocuments]);

  useEffect(() => {
    if (viewMode === 'customer') {
      setSortField('open_amount');
      setSortDirection('desc');
      setExpandedCustomers(new Set());
      return;
    }
    setSortField('document_date');
    setSortDirection('desc');
  }, [viewMode]);

  useEffect(() => {
    const maybeRefreshDirtyData = () => {
      const dirtyAt = getRivhitDocumentsDirtyAt();
      if (!dirtyAt || dirtyAt === handledDirtyAtRef.current) return;
      handledDirtyAtRef.current = dirtyAt;
      refreshPageData().finally(() => {
        clearRivhitDocumentsDirty();
      });
    };

    maybeRefreshDirtyData();
    return subscribeToRivhitDocumentsDirty(maybeRefreshDirtyData);
  }, [refreshPageData]);

  const filterChips: { id: CategoryFilter; labelKey: string }[] = [
    { id: 'all', labelKey: 'documents.filterAll' },
    { id: 'invoice', labelKey: 'documents.filterInvoices' },
    { id: 'receipt', labelKey: 'documents.filterReceipts' },
    { id: 'delivery_note', labelKey: 'documents.filterDeliveryNotes' },
  ];

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);

  const processedDocuments = useMemo(() => {
    return documents;
  }, [documents]);

  const sortedDocuments = useMemo(() => {
    const items = [...processedDocuments];
    items.sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'document_number':
          result = a.document_number - b.document_number;
          break;
        case 'customer_name':
          result = (a.customer_name || '').localeCompare(b.customer_name || '', 'he');
          break;
        case 'amount':
          result = a.amount - b.amount;
          break;
        case 'open_amount':
          result = (a.open_amount || 0) - (b.open_amount || 0);
          break;
        case 'invoices_count':
          result = (a.invoices_count || 0) - (b.invoices_count || 0);
          break;
        case 'document_date':
        default:
          result = parseDocumentDate(a.document_date) - parseDocumentDate(b.document_date);
          if (result === 0) {
            result = parseDocumentTime(a.document_time) - parseDocumentTime(b.document_time);
          }
          if (result === 0) {
            result = a.document_number - b.document_number;
          }
          break;
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return items;
  }, [processedDocuments, sortDirection, sortField]);

  const pageStart = totalCount === 0 ? 0 : pageOffset + 1;
  const pageEnd = Math.min(pageOffset + processedDocuments.length, totalCount);

  const handleSort = (field: DocumentSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'document_date' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (field: DocumentSortField) => {
    if (field !== sortField) return <span className="text-gray-300">↕</span>;
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const isCustomerView = viewMode === 'customer';

  const toggleCustomerExpand = async (customerId: number) => {
    if (!customerId) return;

    let shouldLoad = false;
    setExpandedCustomers(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else {
        next.add(customerId);
        shouldLoad = !customerDocuments[customerId];
      }
      return next;
    });

    if (!shouldLoad || customerDocumentsLoading.has(customerId)) return;

    setCustomerDocumentsLoading(prev => new Set(prev).add(customerId));
    try {
      const params = {
        ...buildParams(),
        customer_id: customerId,
        category_filter: 'invoice',
        group_by: undefined,
        offset: 0,
        limit: 500,
      };
      const res = await listAllDocuments(params as Record<string, string | number | boolean | undefined>);
      const items = (res.data.items ?? []).map((item: UnifiedDocumentItem) => {
        const rememberedLink = getRememberedRivhitDocumentLink(item.document_type, item.document_number);
        return {
          ...item,
          document_link: item.document_link || rememberedLink,
        };
      });
      setCustomerDocuments(prev => ({ ...prev, [customerId]: items }));
    } catch (err) {
      console.error('[Documents] Failed to load customer documents:', err);
      setCustomerDocuments(prev => ({ ...prev, [customerId]: [] }));
    } finally {
      setCustomerDocumentsLoading(prev => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    }
  };

  const docExpandKey = (doc: UnifiedDocumentItem) => `${doc.document_type}-${doc.document_number}`;

  const toggleDocExpand = async (doc: UnifiedDocumentItem) => {
    const key = docExpandKey(doc);
    if (expandedDocs.has(key)) {
      setExpandedDocs(prev => { const next = new Set(prev); next.delete(key); return next; });
      return;
    }
    setExpandedDocs(prev => new Set(prev).add(key));
    if (docLineItems[key]) return; // already fetched

    setDocLineItemsLoading(prev => new Set(prev).add(key));
    try {
      const res = await getDocumentDetails(doc.document_type, doc.document_number);
      setDocLineItems(prev => ({ ...prev, [key]: res.data.items }));
    } catch (err) {
      console.error('[Documents] Failed to load document details:', err);
      setDocLineItems(prev => ({ ...prev, [key]: [] }));
    } finally {
      setDocLineItemsLoading(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const handleViewDocument = (documentLink?: string) => {
    if (!documentLink) return;
    window.open(documentLink, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = (documentLink?: string) => {
    if (!documentLink) return;
    const link = window.document.createElement('a');
    link.href = documentLink;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const handleCustomerClick = (doc: UnifiedDocumentItem) => {
    if (!doc.customer_id) return;
    const customer = customers.find((candidate) => candidate.customer_id === doc.customer_id);
    if (!customer) return;
    setSelectedCustomer(customer);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('documents.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('documents.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshPageData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={t('common.refresh', 'Refresh')}
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            {t('common.refresh', 'Refresh')}
          </button>
          <button
            onClick={() => navigate('/create-document')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            {t('documents.newDocument')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('documents.filtersTitle', 'Filters')}</h3>

        {/* Row 1: Category chips + view toggles */}
        <div className="flex flex-wrap items-center gap-2">
          {filterChips.map(chip => (
            <button
              key={chip.id}
              onClick={() => setCategoryFilter(chip.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                categoryFilter === chip.id
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
              }`}
            >
              {t(chip.labelKey)}
            </button>
          ))}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={viewMode === 'customer'}
              onChange={() => setViewMode(viewMode === 'customer' ? 'documents' : 'customer')}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">{t('documents.viewByCustomer', 'By customer')}</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={openOnly}
              onChange={() => setOpenOnly((prev) => !prev)}
              className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-sm text-gray-700">{t('documents.openInvoicesOnly', 'Open invoices only')}</span>
          </label>
        </div>

        {/* Row 2: Date inputs + quick ranges */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('documents.dateFrom')}</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={e => handleDateFromChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('documents.dateTo')}</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={e => handleDateToChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </label>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{t('documents.quickRange', 'Quick range')}:</span>
            {([
              { key: 'today', label: t('documents.rangeToday', 'Today'), from: getTodayIsoDate(), to: getTodayIsoDate() },
              { key: 'yesterday', label: t('documents.rangeYesterday', 'Yesterday'), from: getDaysAgoIsoDate(1), to: getDaysAgoIsoDate(1) },
              { key: 'week', label: t('documents.rangeThisWeek', 'This week'), from: getStartOfWeek(), to: getTodayIsoDate() },
              { key: 'month', label: t('documents.rangeThisMonth', 'This month'), from: getStartOfMonth(), to: getTodayIsoDate() },
              { key: 'last30', label: t('documents.rangeLast30Days', 'Last 30 days'), from: getDaysAgoIsoDate(29), to: getTodayIsoDate() },
              { key: 'prevMonth', label: t('documents.rangePreviousMonth', 'Previous month'), from: getStartOfPreviousMonth(), to: getEndOfPreviousMonth() },
            ] as const).map(range => {
              const isActive = dateFrom === range.from && dateTo === range.to;
              return (
                <button
                  key={range.key}
                  type="button"
                  onClick={() => { setDateFrom(range.from); setDateTo(range.to); }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {range.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: Customer search */}
        <input
          type="text"
          value={customerSearch}
          onChange={e => setCustomerSearch(e.target.value)}
          placeholder={t('documents.searchByCustomer')}
          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </div>

      {/* Documents table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {t('documents.loading')}
          </div>
        ) : sortedDocuments.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {t('documents.noDocuments')}
          </div>
        ) : (
          <>
            {/* Count */}
            <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {pageStart}-{pageEnd} {t('common.of', 'of')} {totalCount} {isCustomerView ? t('documents.customersCountLabel', 'customers') : t('documents.title').toLowerCase()}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => Math.max(prev - DOCUMENTS_PAGE_SIZE, 0))}
                  disabled={loading || pageOffset === 0}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.previous', 'Previous')}
                </button>
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => prev + DOCUMENTS_PAGE_SIZE)}
                  disabled={loading || !hasMore}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                    {isCustomerView && <th className="w-8 px-2 py-3" />}
                    <th className="px-4 py-3 text-start font-medium">
                      <button type="button" onClick={() => handleSort('customer_name')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                        <span>{t('documents.customer')}</span>
                        {renderSortIndicator('customer_name')}
                      </button>
                    </th>
                    {isCustomerView ? (
                      <>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('invoices_count')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.invoicesCount', 'Invoices')}</span>
                            {renderSortIndicator('invoices_count')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.invoicesAmount', 'Invoices amount')}</span>
                            {renderSortIndicator('amount')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">{t('documents.paidAmount', 'Paid')}</th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('open_amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.openAmount', 'Open')}</span>
                            {renderSortIndicator('open_amount')}
                          </button>
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="w-8 px-2 py-3" />
                        <th className="px-4 py-3 text-start font-medium">{t('documents.type')}</th>
                        <th className="px-4 py-3 text-start font-medium">
                          <button type="button" onClick={() => handleSort('document_number')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.docNumber')}</span>
                            {renderSortIndicator('document_number')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-start font-medium">
                          <button type="button" onClick={() => handleSort('document_date')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.date')}</span>
                            {renderSortIndicator('document_date')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.amount')}</span>
                            {renderSortIndicator('amount')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">{t('documents.openAmount', 'Open')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('common.actions', 'Actions')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedDocuments.map((doc) => {
                    const badge = CATEGORY_BADGES[doc.document_category] || CATEGORY_BADGES.invoice;
                    const isExpanded = isCustomerView && expandedCustomers.has(doc.customer_id || 0);
                    const customerId = doc.customer_id || 0;
                    const childDocs = customerDocuments[customerId] || doc.documents || [];
                    const isCustomerDocsLoading = customerDocumentsLoading.has(customerId);
                    const customerRowKey = [
                      'customer',
                      doc.customer_id ?? 'unknown',
                      doc.customer_name || 'unnamed',
                      doc.invoices_count || 0,
                      doc.amount || 0,
                      doc.open_amount || 0,
                    ].join('-');
                    const customerChildrenKey = `${customerRowKey}-children`;
                    const documentRowKey = `${doc.document_category}-${doc.document_type}-${doc.document_number}-${doc.document_date}-${doc.document_time || 'na'}`;
                    return isCustomerView ? (
                      <React.Fragment key={customerRowKey}>{/* Customer aggregate row */}
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-2 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => toggleCustomerExpand(customerId)}
                              className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 transition-colors text-gray-500"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {doc.customer_id ? (
                              <button
                                type="button"
                                onClick={() => handleCustomerClick(doc)}
                                className="text-start text-primary-700 underline-offset-2 transition-colors hover:text-primary-800 hover:underline disabled:cursor-default disabled:text-gray-700 disabled:no-underline font-semibold"
                                disabled={!customers.some((candidate) => candidate.customer_id === doc.customer_id)}
                              >
                                {doc.customer_name || `#${doc.customer_id}`}
                              </button>
                            ) : (
                              <span className="font-semibold">{doc.customer_name || '—'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-end text-gray-700">{doc.invoices_count || 0}</td>
                          <td className="px-4 py-3 text-end font-medium text-gray-900">{formatAmount(doc.amount)}</td>
                          <td className="px-4 py-3 text-end text-green-700">{formatAmount(doc.paid_amount || 0)}</td>
                          <td className="px-4 py-3 text-end font-semibold text-amber-700">{formatAmount(doc.open_amount || 0)}</td>
                        </tr>
                        {/* Expanded child document rows */}
                        {isExpanded && (
                          <tr key={customerChildrenKey}>
                            <td colSpan={6} className="p-0">
                              {isCustomerDocsLoading ? (
                                <div className="px-12 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.loading')}</div>
                              ) : childDocs.length > 0 ? (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-100/60 text-gray-500 text-xs uppercase tracking-wider">
                                      <th className="w-8 px-2 py-2" />
                                      <th className="px-4 py-2 text-start font-medium">{t('documents.docNumber')}</th>
                                      <th className="px-4 py-2 text-start font-medium">{t('documents.date')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('documents.amount')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('documents.openAmount', 'Open')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('common.actions', 'Actions')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-50">
                                    {childDocs.map((child) => {
                                    const childKey = docExpandKey(child);
                                    const isChildExpanded = expandedDocs.has(childKey);
                                    const childLineItems = docLineItems[childKey];
                                    const isChildLoading = docLineItemsLoading.has(childKey);
                                    return (
                                      <React.Fragment key={`child-${child.document_category}-${childKey}-${child.document_date}-${child.document_time || 'na'}`}>
                                        <tr className="bg-gray-50/50 hover:bg-gray-100/50 transition-colors cursor-pointer" onClick={() => toggleDocExpand(child)}>
                                          <td className="w-8 px-2 py-2 text-center">
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); toggleDocExpand(child); }}
                                              className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 transition-colors text-gray-400"
                                              aria-label={isChildExpanded ? 'Collapse' : 'Expand'}
                                            >
                                              {isChildLoading ? (
                                                <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                                              ) : (
                                                <span className={`inline-block text-xs transition-transform ${isChildExpanded ? 'rotate-90' : ''}`}>▶</span>
                                              )}
                                            </button>
                                          </td>
                                          <td className="px-4 py-2 text-gray-900 font-medium">{child.document_number}</td>
                                          <td className="px-4 py-2 text-gray-600">
                                            <div>{child.document_date}</div>
                                            {child.document_time && (
                                              <div className="text-xs text-gray-400">{child.document_time}</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-end font-medium text-gray-900">{formatAmount(child.amount)}</td>
                                          <td className="px-4 py-2 text-end font-semibold text-amber-700">{formatAmount(child.open_amount || 0)}</td>
                                          <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleViewDocument(child.document_link); }}
                                                disabled={!child.document_link}
                                                className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                              >
                                                {t('documents.viewDocument', 'View')}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleDownloadDocument(child.document_link); }}
                                                disabled={!child.document_link}
                                                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                              >
                                                {t('documents.downloadDocument', 'Download')}
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                        {isChildExpanded && (
                                          <tr>
                                            <td colSpan={6} className="p-0">
                                              {isChildLoading ? (
                                                <div className="px-12 py-2 text-xs text-gray-400 bg-gray-100/40">{t('documents.loadingItems', 'Loading items...')}</div>
                                              ) : childLineItems && childLineItems.length > 0 ? (
                                                <table className="w-full text-xs">
                                                  <thead>
                                                    <tr className="bg-gray-100/40 text-gray-400 uppercase tracking-wider">
                                                      <th className="w-12 px-2 py-1.5" />
                                                      <th className="px-4 py-1.5 text-start font-medium">{t('documents.itemDescription', 'Description')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemQuantity', 'Qty')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemPrice', 'Price')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemTotal', 'Total')}</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-gray-50">
                                                    {childLineItems.map((item, iIdx) => (
                                                      <tr key={`cline-${iIdx}`} className="bg-white/60 hover:bg-gray-50/80 transition-colors">
                                                        <td className="w-12 px-2 py-1.5" />
                                                        <td className="px-4 py-1.5 text-gray-800">{item.description || '—'}</td>
                                                        <td className="px-4 py-1.5 text-end text-gray-600">{item.quantity}</td>
                                                        <td className="px-4 py-1.5 text-end text-gray-600">{item.price_nis != null ? formatAmount(item.price_nis) : '—'}</td>
                                                        <td className="px-4 py-1.5 text-end font-medium text-gray-800">{item.total_nis != null ? formatAmount(item.total_nis) : '—'}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              ) : (
                                                <div className="px-12 py-2 text-xs text-gray-400 bg-gray-100/40">{t('documents.noItems', 'No line items')}</div>
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                    })}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="px-12 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.noDocuments')}</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={documentRowKey}>{/* Document row with expand */}
                        {(() => {
                          const dKey = docExpandKey(doc);
                          const isDocExpanded = expandedDocs.has(dKey);
                          const lineItems = docLineItems[dKey];
                          const isLineItemsLoading = docLineItemsLoading.has(dKey);
                          return (
                            <>
                              <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleDocExpand(doc)}>
                                <td className="px-2 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleDocExpand(doc); }}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 transition-colors text-gray-500"
                                    aria-label={isDocExpanded ? 'Collapse' : 'Expand'}
                                  >
                                    {isLineItemsLoading ? (
                                      <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                                    ) : (
                                      <span className={`inline-block transition-transform ${isDocExpanded ? 'rotate-90' : ''}`}>▶</span>
                                    )}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {doc.customer_id ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleCustomerClick(doc); }}
                                      className="text-start text-primary-700 underline-offset-2 transition-colors hover:text-primary-800 hover:underline disabled:cursor-default disabled:text-gray-700 disabled:no-underline"
                                      disabled={!customers.some((candidate) => candidate.customer_id === doc.customer_id)}
                                    >
                                      {doc.customer_name || `#${doc.customer_id}`}
                                    </button>
                                  ) : (
                                    doc.customer_name || '—'
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.bg} ${badge.text}`}>
                                    {t(badge.labelKey)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  {doc.document_number}
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  <div>{doc.document_date}</div>
                                  {doc.document_time && (
                                    <div className="text-xs text-gray-400">{doc.document_time}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-end font-medium text-gray-900">
                                  {formatAmount(doc.amount)}
                                </td>
                                <td className="px-4 py-3 text-end font-semibold text-amber-700">
                                  {doc.document_category === 'invoice' ? formatAmount(doc.open_amount || 0) : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleViewDocument(doc.document_link); }}
                                      disabled={!doc.document_link}
                                      className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                      {t('documents.viewDocument', 'View')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleDownloadDocument(doc.document_link); }}
                                      disabled={!doc.document_link}
                                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                      {t('documents.downloadDocument', 'Download')}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {isDocExpanded && (
                                <tr key={`items-${documentRowKey}`}>
                                  <td colSpan={8} className="p-0">
                                    {isLineItemsLoading ? (
                                      <div className="px-8 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.loadingItems', 'Loading items...')}</div>
                                    ) : lineItems && lineItems.length > 0 ? (
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-gray-100/60 text-gray-500 text-xs uppercase tracking-wider">
                                            <th className="w-8 px-2 py-2" />
                                            <th className="px-4 py-2 text-start font-medium">{t('documents.itemDescription', 'Description')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemQuantity', 'Qty')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemPrice', 'Price')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemTotal', 'Total')}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {lineItems.map((item, iIdx) => (
                                            <tr key={`line-${iIdx}`} className="bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                                              <td className="w-8 px-2 py-2" />
                                              <td className="px-4 py-2 text-gray-900">{item.description || '—'}</td>
                                              <td className="px-4 py-2 text-end text-gray-700">{item.quantity}</td>
                                              <td className="px-4 py-2 text-end text-gray-700">{item.price_nis != null ? formatAmount(item.price_nis) : '—'}</td>
                                              <td className="px-4 py-2 text-end font-medium text-gray-900">{item.total_nis != null ? formatAmount(item.total_nis) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <div className="px-8 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.noItems', 'No line items')}</div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </>
        )}
      </div>

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
}
